import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "../store/useStore";
import { useMenu } from "../data/menu";
import { getUpsell, type Upsell as UpsellType } from "../lib/api";
import { t } from "../lib/i18n";
import { LANGUAGES } from "../data/languages";

export default function Upsell() {
  const lang = useStore((s) => s.language);
  const cart = useStore((s) => s.cart);
  const addToCart = useStore((s) => s.addToCart);
  const navigate = useNavigate();
  const [offer, setOffer] = useState<UpsellType>(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(false);
  const isRtl = LANGUAGES.find((l) => l.code === lang)?.rtl;
  const { findProduct } = useMenu();

  useEffect(() => {
    let cancelled = false;
    // Hard fallback if the model takes too long
    const timeout = setTimeout(() => {
      if (!cancelled) { setLoading(false); navigate("/kiosk/review"); }
    }, 5000);

    getUpsell({ cart, language: lang || "es" })
      .then((o) => {
        if (cancelled) return;
        clearTimeout(timeout);
        if (!o) { navigate("/kiosk/review"); return; }
        setOffer(o);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        clearTimeout(timeout);
        navigate("/kiosk/review");
      });

    return () => { cancelled = true; clearTimeout(timeout); };
  }, []); // eslint-disable-line

  if (loading) return <div className="grain min-h-screen flex items-center justify-center bg-paper text-ink/40 font-mono text-sm tracking-widest">…</div>;
  if (!offer) return null;
  const product = findProduct(offer.sku);
  if (!product) { navigate("/kiosk/review"); return null; }

  return (
    <div
      className="grain min-h-screen w-full bg-paper flex flex-col items-center justify-center px-8 relative"
      dir={isRtl ? "rtl" : undefined}
    >
      <p className="font-hand text-3xl text-mezcal mb-2">{t(lang, "upsell_one_more")}</p>
      <p className="font-mono text-xs tracking-[0.4em] text-ink/40 uppercase mb-12">— {t(lang, "upsell_ps")} —</p>

      {!opened ? (
        <button
          onClick={() => setOpened(true)}
          className="relative w-80 h-56 active:scale-95 transition"
        >
          <div className="absolute inset-0 bg-sal border-4 border-ink rounded-sm" style={{ transform: "rotate(-3deg)" }}>
            <div className="absolute inset-0" style={{
              clipPath: "polygon(0 0, 50% 60%, 100% 0)",
              background: "#1A1612",
            }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-hibisco border-4 border-paper flex items-center justify-center shadow-md">
              <span className="font-display text-paper text-3xl font-bold">i</span>
            </div>
          </div>
          <div className="absolute -bottom-8 left-0 right-0 text-center font-mono text-xs tracking-widest text-ink/60">
            {t(lang, "upsell_tap_open")}
          </div>
        </button>
      ) : (
        <div className="max-w-xl w-full bg-paper border-4 border-ink p-8 relative animate-rise" style={{ transform: "rotate(-1deg)" }}>
          <span className="absolute -top-3 left-6 bg-paper px-3 font-mono text-[10px] tracking-widest">{t(lang, "upsell_from_chef")}</span>
          <div className="flex items-center gap-6">
            <div className="text-7xl">{product.image}</div>
            <div className="flex-1">
              <h2 className="font-display font-black text-3xl leading-tight">{product.name}</h2>
              <p className="font-sans text-ink/70 mt-1">{product.description}</p>
              <p className="font-mono text-2xl font-bold text-mezcal mt-2">${product.price}</p>
            </div>
          </div>
          <div className="mt-6 border-t border-dashed border-ink/30 pt-5 font-display italic text-2xl text-center leading-snug">
            “{offer.copy}”
          </div>
          <div className="mt-7 flex gap-3">
            <button
              onClick={() => navigate("/kiosk/review")}
              className="flex-1 py-4 rounded-sm border-2 border-ink/30 font-display font-bold"
            >
              {t(lang, "upsell_no_thanks")}
            </button>
            <button
              onClick={() => { addToCart(product, 1); navigate("/kiosk/review"); }}
              className="flex-[2] py-4 rounded-sm bg-mango text-ink font-display font-bold tracking-wide"
            >
              {t(lang, "upsell_yes_add")} · ${product.price}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
